import React from 'react'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import styled, { keyframes } from 'styled-components'
import { useParams, Link } from 'react-router-dom'
import { FiClock } from 'react-icons/fi'
import cacheManager from '../utils/cacheManager'

/**
 * Strip promotional linkout content from recipe summary
 * Removes sentences that link out to Spoonacular similar recipes
 */
const cleanSummary = (html) => {
  if (!html) return '';

  // Patterns to remove (promotional content typically at end of summary)
  const patternsToRemove = [
    // "If you like this recipe, take a look at these similar recipes:" followed by links
    /If you like this recipe,?\s*(you might also like|take a look at|try)\s*(these similar recipes)?:?\s*(<a[^>]*>.*?<\/a>[,\s]*)+\.?/gi,
    // "Try [similar recipes]:" patterns
    /Try\s*(<a[^>]*>.*?<\/a>[,\s]*(and\s*)?)+\.?/gi,
    // "Similar recipes:" followed by links
    /Similar recipes:?\s*(<a[^>]*>.*?<\/a>[,\s]*(and\s*)?)+\.?/gi,
    // Any sentence containing spoonacular.com links
    /[^.]*<a[^>]*href="[^"]*spoonacular\.com[^"]*"[^>]*>.*?<\/a>[^.]*\.?/gi,
    // Clean up trailing punctuation and whitespace
    /[,\s]+$/g,
  ];

  let cleaned = html;
  for (const pattern of patternsToRemove) {
    cleaned = cleaned.replace(pattern, '');
  }

  // Clean up any double spaces or trailing whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
};

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Similar Recipe Card component - hides itself if image fails to load
const SimilarRecipeCard = ({ recipe }) => {
  const [imageError, setImageError] = useState(false);

  if (imageError) return null;

  return (
    <SimilarCardWrapper to={`/recipe/${recipe.id}`}>
      <SimilarImage
        src={`https://img.spoonacular.com/recipes/${recipe.id}-312x231.jpg`}
        alt={recipe.title}
        onError={() => setImageError(true)}
      />
      <SimilarTitle>{recipe.title}</SimilarTitle>
      <SimilarMeta>
        {recipe.readyInMinutes && <span><FiClock style={{marginRight: '4px', verticalAlign: 'middle'}} /> {recipe.readyInMinutes}m</span>}
      </SimilarMeta>
    </SimilarCardWrapper>
  );
};

function Recipe() {

  let params = useParams();
  const [details, setDetails] = useState({});
  const [analyzedInstructions, setAnalyzedInstructions] = useState([]);
  const [similarRecipes, setSimilarRecipes] = useState([]);
  const [activeTab, setActiveTab] = useState('instructions');
  const [loading, setLoading] = useState(true);
  const [servings, setServings] = useState(null);

  const fetchDetails = useCallback(async () => {
    setLoading(true);

    // Check cache first for recipe details
    const cachedDetails = await cacheManager.get(params.name, 'RECIPE_DETAIL');
    const cachedInstructions = await cacheManager.get(`${params.name}_instructions`, 'RECIPE_DETAIL');
    const cachedSimilar = await cacheManager.get(`${params.name}_similar`, 'RECIPE_DETAIL');

    if (cachedDetails) {
      console.log('Using cached recipe details for:', params.name);
      setDetails(cachedDetails);
      setServings(cachedDetails.servings || 1);
      if (cachedInstructions) {
        setAnalyzedInstructions(cachedInstructions);
      }
      if (cachedSimilar) {
        // Filter similar recipes to only include those with valid images
        const filteredSimilar = cachedSimilar.filter(recipe => recipe.imageType);
        setSimilarRecipes(filteredSimilar);
      }
      setLoading(false);
      return;
    }

    try {
      // Fetch recipe info, analyzed instructions, and similar recipes in parallel
      const [infoResponse, instructionsResponse, similarResponse] = await Promise.all([
        fetch(`https://api.spoonacular.com/recipes/${params.name}/information?apiKey=${process.env.REACT_APP_API_KEY}&includeNutrition=true`),
        fetch(`https://api.spoonacular.com/recipes/${params.name}/analyzedInstructions?stepBreakdown=true&apiKey=${process.env.REACT_APP_API_KEY}`),
        fetch(`https://api.spoonacular.com/recipes/${params.name}/similar?apiKey=${process.env.REACT_APP_API_KEY}&number=6`)
      ]);

      const detailData = await infoResponse.json();
      const instructionsData = await instructionsResponse.json();
      const similarData = await similarResponse.json();

      // Cache the recipe details (30 day TTL)
      await cacheManager.set(params.name, detailData, 'RECIPE_DETAIL');
      await cacheManager.set(`${params.name}_instructions`, instructionsData, 'RECIPE_DETAIL');
      await cacheManager.set(`${params.name}_similar`, similarData, 'RECIPE_DETAIL');

      setDetails(detailData);
      setAnalyzedInstructions(instructionsData);
      // Filter similar recipes to only include those with valid images
      const filteredSimilar = (similarData || []).filter(recipe => recipe.imageType);
      setSimilarRecipes(filteredSimilar);
      setServings(detailData.servings || 1);
    } catch (error) {
      console.error('Error fetching recipe details:', error);
    } finally {
      setLoading(false);
    }
  }, [params.name]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  // Serving adjustment logic
  const baseServings = details.servings || 1;
  const currentServings = servings ?? baseServings;
  const scaleFactor = currentServings / baseServings;

  const adjustServings = (delta) => {
    const newServings = Math.max(1, currentServings + delta);
    setServings(newServings);
  };

  // Scale ingredient amount
  const scaleAmount = (amount) => {
    if (!amount) return '';
    const scaled = amount * scaleFactor;
    // Round to nice fractions
    if (scaled === Math.floor(scaled)) {
      return scaled.toString();
    }
    // Handle common fractions
    const decimal = scaled - Math.floor(scaled);
    const floor = Math.floor(scaled);
    if (Math.abs(decimal - 0.25) < 0.05) return floor ? `${floor} ¼` : '¼';
    if (Math.abs(decimal - 0.33) < 0.05) return floor ? `${floor} ⅓` : '⅓';
    if (Math.abs(decimal - 0.5) < 0.05) return floor ? `${floor} ½` : '½';
    if (Math.abs(decimal - 0.67) < 0.05) return floor ? `${floor} ⅔` : '⅔';
    if (Math.abs(decimal - 0.75) < 0.05) return floor ? `${floor} ¾` : '¾';
    return scaled.toFixed(1).replace(/\.0$/, '');
  };

  // Format scaled ingredient
  const formatIngredient = (ingredient) => {
    if (!ingredient.amount || ingredient.amount === 0) {
      return ingredient.original;
    }
    const scaledAmount = scaleAmount(ingredient.amount);
    const unit = ingredient.unit || '';
    const name = ingredient.name || '';
    return `${scaledAmount} ${unit} ${name}`.trim();
  };

  // Parse paragraph instructions into steps when API doesn't provide structured data
  const parseInstructionsToSteps = (htmlInstructions) => {
    if (!htmlInstructions) return [];

    // Remove HTML tags
    const text = htmlInstructions.replace(/<[^>]*>/g, '');

    // Split by common sentence patterns
    // Look for: periods followed by space and capital letter, or numbered steps
    const steps = text
      .split(/(?<=\.)\s+(?=[A-Z])|(?<=\d\.)\s*/)
      .map(s => s.trim())
      .filter(s => s.length > 10) // Filter out very short fragments
      .map((step, index) => ({
        number: index + 1,
        step: step.replace(/^\d+\.\s*/, ''), // Remove leading numbers
        ingredients: [],
        equipment: []
      }));

    return steps.length > 1 ? steps : [];
  };

  // Get parsed steps from either API or fallback parser
  const parsedSteps = useMemo(() => {
    // First try analyzedInstructions from API
    if (analyzedInstructions?.length > 0 && analyzedInstructions[0]?.steps?.length > 0) {
      return { steps: analyzedInstructions[0].steps, isFromApi: true };
    }
    // Fallback: parse the raw instructions
    const fallbackSteps = parseInstructionsToSteps(details.instructions);
    if (fallbackSteps.length > 0) {
      return { steps: fallbackSteps, isFromApi: false };
    }
    return { steps: [], isFromApi: false };
  }, [analyzedInstructions, details.instructions]);

  // Extract unique equipment from instructions
  const equipment = useMemo(() => {
    if (!analyzedInstructions?.length) return [];
    const equipmentMap = new Map();
    analyzedInstructions[0]?.steps?.forEach(step => {
      step.equipment?.forEach(eq => {
        if (!equipmentMap.has(eq.id)) {
          // The API returns image as filename like "pot.png" - construct full URL
          let imageUrl = null;
          if (eq.image) {
            // Handle both full URLs and just filenames
            if (eq.image.startsWith('http')) {
              imageUrl = eq.image;
            } else {
              imageUrl = `https://spoonacular.com/cdn/equipment_100x100/${eq.image}`;
            }
          }
          equipmentMap.set(eq.id, {
            id: eq.id,
            name: eq.name,
            image: imageUrl
          });
        }
      });
    });
    return Array.from(equipmentMap.values());
  }, [analyzedInstructions]);

  // Copy link to clipboard
  const handleShare = async () => {
    const url = window.location.href;
    const shareText = `🍽️ ${details.title}\n\n${details.image}\n\n${url}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: details.title,
          text: `Check out this recipe: ${details.title}`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(shareText);
        alert('Recipe link copied! Paste to share with the image URL.');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  if (loading || !details.title) {
    return (
      <DetailWrapper>
        <ImageSection>
          <ShimmerImage />
          <ShimmerTitle />
          <ShimmerMeta>
            <ShimmerBadge />
            <ShimmerBadge />
            <ShimmerBadge />
          </ShimmerMeta>
        </ImageSection>
        <ContentSection>
          <ShimmerTabs>
            <ShimmerTab />
            <ShimmerTab />
            <ShimmerTab />
          </ShimmerTabs>
          <ShimmerContent>
            <ShimmerParagraph />
            <ShimmerParagraph style={{ width: '90%' }} />
            <ShimmerParagraph style={{ width: '75%' }} />
            <ShimmerDivider />
            <ShimmerParagraph />
            <ShimmerParagraph style={{ width: '85%' }} />
            <ShimmerParagraph style={{ width: '60%' }} />
          </ShimmerContent>
        </ContentSection>
      </DetailWrapper>
    );
  }

  // Generate description for meta tags (cleaned of promotional content)
  const metaDescription = details.summary
    ? cleanSummary(details.summary).replace(/<[^>]*>/g, '').substring(0, 160) + '...'
    : `Check out this delicious ${details.title} recipe on Yummies!`;

  return (
    <DetailWrapper>
      {/* Dynamic OG Meta Tags for Social Sharing */}
      <Helmet>
        <title>{details.title} | Yummies</title>
        <meta name="description" content={metaDescription} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://thedanielyeh.github.io/recipe-react/recipe/${params.name}`} />
        <meta property="og:title" content={`${details.title} | Yummies`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={details.image} />
        <meta property="og:image:width" content="556" />
        <meta property="og:image:height" content="370" />
        <meta property="og:site_name" content="Yummies" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://thedanielyeh.github.io/recipe-react/recipe/${params.name}`} />
        <meta name="twitter:title" content={`${details.title} | Yummies`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={details.image} />
      </Helmet>

      <ImageSection>
        <RecipeImage src={details.image} alt={details.title} />
        <TitleRow>
          <RecipeTitle>{details.title}</RecipeTitle>
          <ShareButton onClick={handleShare} title="Share this recipe">
            <ShareIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </ShareIcon>
            <ShareText>Share</ShareText>
          </ShareButton>
        </TitleRow>
        <RecipeMeta>
          {details.readyInMinutes != null && details.readyInMinutes > 0 && (
            <MetaItem>
              <MetaIcon><FiClock /></MetaIcon>
              <MetaText>{details.readyInMinutes} min</MetaText>
            </MetaItem>
          )}
          {details.spoonacularScore != null && (
            <MetaItem>
              <MetaIcon>⭐</MetaIcon>
              <MetaText>{Math.round(details.spoonacularScore)}/100</MetaText>
            </MetaItem>
          )}
          {details.pricePerServing != null && (
            <MetaItem>
              <MetaIcon>💰</MetaIcon>
              <MetaText>${(details.pricePerServing / 100).toFixed(2)}/serving</MetaText>
            </MetaItem>
          )}
        </RecipeMeta>

        {/* Serving Adjuster */}
        <ServingContainer>
          <ServingLabel>Servings</ServingLabel>
          <ServingControls>
            <ServingButton
              onClick={() => adjustServings(-1)}
              disabled={currentServings <= 1}
            >
              −
            </ServingButton>
            <ServingValue>{currentServings}</ServingValue>
            <ServingButton onClick={() => adjustServings(1)}>
              +
            </ServingButton>
          </ServingControls>
          {scaleFactor !== 1 && (
            <ScaleIndicator>
              {scaleFactor > 1 ? '↑' : '↓'} {scaleFactor.toFixed(1)}x
            </ScaleIndicator>
          )}
        </ServingContainer>

        {details.diets && details.diets.length > 0 && (
          <DietTags>
            {details.diets.map((diet, index) => (
              <DietTag key={index}>{diet}</DietTag>
            ))}
          </DietTags>
        )}
      </ImageSection>
      <ContentSection>
        <TabContainer>
          <TabButton
            active={activeTab === 'instructions'}
            onClick={() => setActiveTab("instructions")}
          >
            Instructions
          </TabButton>
          <TabButton
            active={activeTab === 'ingredients'}
            onClick={() => setActiveTab("ingredients")}
          >
            Ingredients
            {scaleFactor !== 1 && <ScaledBadge>Scaled</ScaledBadge>}
          </TabButton>
          <TabButton
            active={activeTab === 'nutrition'}
            onClick={() => setActiveTab("nutrition")}
          >
            Nutrition
          </TabButton>
          <TabButton
            active={activeTab === 'details'}
            onClick={() => setActiveTab("details")}
          >
            Details
          </TabButton>
        </TabContainer>
        <TabContent>
          {activeTab === 'instructions' && (
            <InstructionsContent>
              {details.summary && (
                <SummarySection dangerouslySetInnerHTML={{__html: cleanSummary(details.summary)}} />
              )}
              {/* Use parsed steps (from API or fallback parser) */}
              {parsedSteps.steps.length > 0 ? (
                <>
                  <Divider />
                  <SectionTitle>Step-by-Step Instructions</SectionTitle>
                  <StepsList>
                    {parsedSteps.steps.map((step) => (
                      <StepItem key={step.number}>
                        <StepNumber>{step.number}</StepNumber>
                        <StepContent>
                          <StepText>{step.step}</StepText>
                          {step.ingredients && step.ingredients.length > 0 && (
                            <StepMeta>
                              <StepMetaLabel>Ingredients:</StepMetaLabel>
                              <StepMetaTags>
                                {step.ingredients.map((ing, idx) => (
                                  <StepMetaTag key={`${ing.id}-${idx}`}>{ing.name}</StepMetaTag>
                                ))}
                              </StepMetaTags>
                            </StepMeta>
                          )}
                          {step.equipment && step.equipment.length > 0 && (
                            <StepMeta>
                              <StepMetaLabel>Equipment:</StepMetaLabel>
                              <StepMetaTags>
                                {step.equipment.map((eq, idx) => (
                                  <StepMetaTag key={`${eq.id}-${idx}`}>{eq.name}</StepMetaTag>
                                ))}
                              </StepMetaTags>
                            </StepMeta>
                          )}
                        </StepContent>
                      </StepItem>
                    ))}
                  </StepsList>
                </>
              ) : details.instructions ? (
                <>
                  <Divider />
                  <SectionTitle>Instructions</SectionTitle>
                  <InstructionsSection dangerouslySetInnerHTML={{__html: details.instructions}} />
                </>
              ) : (
                <EmptyState>No instructions available for this recipe.</EmptyState>
              )}
            </InstructionsContent>
          )}
          {activeTab === 'ingredients' && (
            <IngredientsList>
              {details.extendedIngredients && details.extendedIngredients.length > 0 ? (
                details.extendedIngredients.map((ingredient, index) => (
                  <IngredientItem key={`${ingredient.id}-${index}`}>
                    <IngredientBullet />
                    <IngredientText>{formatIngredient(ingredient)}</IngredientText>
                  </IngredientItem>
                ))
              ) : (
                <EmptyState>No ingredients available</EmptyState>
              )}
            </IngredientsList>
          )}
          {activeTab === 'nutrition' && (
            <NutritionContent>
              {details.nutrition?.nutrients ? (
                <>
                  {/* Main macros */}
                  <SectionTitle>Macronutrients</SectionTitle>
                  <NutritionGrid>
                    {details.nutrition.nutrients
                      .filter(nutrient => ['Calories', 'Protein', 'Fat', 'Carbohydrates', 'Fiber', 'Sugar'].includes(nutrient.name))
                      .map((nutrient, index) => (
                        <NutritionItem key={index}>
                          <NutritionLabel>{nutrient.name}</NutritionLabel>
                          <NutritionValue>
                            {Math.round(nutrient.amount)} {nutrient.unit}
                          </NutritionValue>
                          {nutrient.percentOfDailyNeeds && (
                            <NutritionPercent>{Math.round(nutrient.percentOfDailyNeeds)}% DV</NutritionPercent>
                          )}
                        </NutritionItem>
                      ))}
                  </NutritionGrid>

                  {/* Vitamins & Minerals */}
                  <Divider />
                  <SectionTitle>Vitamins & Minerals</SectionTitle>
                  <NutritionGrid>
                    {details.nutrition.nutrients
                      .filter(nutrient =>
                        ['Vitamin A', 'Vitamin C', 'Vitamin D', 'Vitamin E', 'Vitamin K', 'Vitamin B6', 'Vitamin B12',
                         'Iron', 'Calcium', 'Potassium', 'Magnesium', 'Zinc', 'Sodium', 'Folate'].includes(nutrient.name)
                      )
                      .filter(nutrient => nutrient.amount > 0)
                      .slice(0, 8)
                      .map((nutrient, index) => (
                        <NutritionItem key={index}>
                          <NutritionLabel>{nutrient.name}</NutritionLabel>
                          <NutritionValue>
                            {nutrient.amount < 1 ? nutrient.amount.toFixed(1) : Math.round(nutrient.amount)} {nutrient.unit}
                          </NutritionValue>
                          {nutrient.percentOfDailyNeeds && (
                            <NutritionPercent>{Math.round(nutrient.percentOfDailyNeeds)}% DV</NutritionPercent>
                          )}
                        </NutritionItem>
                      ))}
                  </NutritionGrid>

                  {/* Nutrition properties */}
                  {details.nutrition.properties && details.nutrition.properties.length > 0 && (
                    <>
                      <Divider />
                      <SectionTitle>Nutrition Score</SectionTitle>
                      <NutritionGrid>
                        {details.nutrition.properties.map((prop, index) => (
                          <NutritionItem key={index}>
                            <NutritionLabel>{prop.name}</NutritionLabel>
                            <NutritionValue>{Math.round(prop.amount)}</NutritionValue>
                          </NutritionItem>
                        ))}
                      </NutritionGrid>
                    </>
                  )}
                </>
              ) : (
                <EmptyState>Nutrition information not available for this recipe.</EmptyState>
              )}
            </NutritionContent>
          )}
          {activeTab === 'details' && (
            <DetailsContent>
              {/* Equipment Section */}
              {equipment.length > 0 && (
                <DetailSection>
                  <DetailLabel>Equipment Needed:</DetailLabel>
                  <EquipmentGrid>
                    {equipment.map((eq) => (
                      <EquipmentItem key={eq.id}>
                        {eq.image && (
                          <EquipmentImageContainer>
                            <EquipmentImage src={eq.image} alt={eq.name} />
                          </EquipmentImageContainer>
                        )}
                        <EquipmentName>{eq.name}</EquipmentName>
                      </EquipmentItem>
                    ))}
                  </EquipmentGrid>
                </DetailSection>
              )}

              {/* Taste Profile */}
              {details.taste && (
                <DetailSection>
                  <DetailLabel>Taste Profile:</DetailLabel>
                  <TasteGrid>
                    {Object.entries(details.taste)
                      .filter(([key]) => !key.includes('_'))
                      .map(([key, value]) => (
                        <TasteItem key={key}>
                          <TasteLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</TasteLabel>
                          <TasteBarContainer>
                            <TasteBar style={{ width: `${Math.min(value, 100)}%` }} />
                          </TasteBarContainer>
                          <TasteValue>{Math.round(value)}</TasteValue>
                        </TasteItem>
                      ))}
                  </TasteGrid>
                </DetailSection>
              )}

              {/* Price Info */}
              {details.pricePerServing && (
                <DetailSection>
                  <DetailLabel>Cost Breakdown:</DetailLabel>
                  <PriceInfo>
                    <PriceItem>
                      <PriceLabel>Per Serving</PriceLabel>
                      <PriceValue>${(details.pricePerServing / 100).toFixed(2)}</PriceValue>
                    </PriceItem>
                    <PriceItem>
                      <PriceLabel>Total ({details.servings || 1} servings)</PriceLabel>
                      <PriceValue>${((details.pricePerServing * (details.servings || 1)) / 100).toFixed(2)}</PriceValue>
                    </PriceItem>
                  </PriceInfo>
                </DetailSection>
              )}

              {details.dishTypes && details.dishTypes.length > 0 && (
                <DetailSection>
                  <DetailLabel>Dish Types:</DetailLabel>
                  <DetailTags>
                    {details.dishTypes.map((type, index) => (
                      <DetailTag key={index}>{type}</DetailTag>
                    ))}
                  </DetailTags>
                </DetailSection>
              )}
              {details.cuisines && details.cuisines.length > 0 && (
                <DetailSection>
                  <DetailLabel>Cuisines:</DetailLabel>
                  <DetailTags>
                    {details.cuisines.map((cuisine, index) => (
                      <DetailTag key={index}>{cuisine}</DetailTag>
                    ))}
                  </DetailTags>
                </DetailSection>
              )}
              {details.winePairing && details.winePairing.pairedWines && details.winePairing.pairedWines.length > 0 && (
                <DetailSection>
                  <DetailLabel>Wine Pairing:</DetailLabel>
                  <WinePairingContent>
                    <WineList>
                      {details.winePairing.pairedWines.map((wine, index) => (
                        <WineItem key={index}>{wine}</WineItem>
                      ))}
                    </WineList>
                    {details.winePairing.pairingText && (
                      <WineDescription>{details.winePairing.pairingText}</WineDescription>
                    )}
                  </WinePairingContent>
                </DetailSection>
              )}

              {/* Similar Recipes */}
              {similarRecipes.length > 0 && (
                <DetailSection>
                  <DetailLabel>Similar Recipes:</DetailLabel>
                  <SimilarGrid>
                    {similarRecipes.map((recipe) => (
                      <SimilarRecipeCard key={recipe.id} recipe={recipe} />
                    ))}
                  </SimilarGrid>
                </DetailSection>
              )}
            </DetailsContent>
          )}
        </TabContent>
      </ContentSection>
    </DetailWrapper>
  )
}

const DetailWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: var(--spacing-2xl) auto;
  padding: 0 var(--spacing-lg);
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: var(--spacing-2xl);
  box-sizing: border-box;

  @media screen and (max-width: 900px) {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    margin: var(--spacing-md) 0;
    padding: 0;
    max-width: 100%;
    width: 100%;
  }
`

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  position: sticky;
  top: var(--spacing-lg);
  height: fit-content;
  max-width: 100%;
  min-width: 0;

  @media screen and (max-width: 900px) {
    position: relative;
    top: 0;
    gap: var(--spacing-md);
    width: 100%;
  }
`

const RecipeImage = styled.img`
  width: 100%;
  max-width: 100%;
  border-radius: var(--radius-xl);
  object-fit: cover;
  box-shadow: 0 8px 24px var(--color-shadow);
  aspect-ratio: 1;

  @media screen and (max-width: 900px) {
    border-radius: var(--radius-md);
  }
`

const RecipeTitle = styled.h2`
  font-size: 2rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  line-height: 1.2;
  margin: 0;
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media screen and (max-width: 900px) {
    font-size: 1.5rem;
    text-align: center;
  }

  @media screen and (max-width: 480px) {
    font-size: 1.25rem;
  }
`

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  justify-content: space-between;

  @media screen and (max-width: 900px) {
    flex-direction: column;
    align-items: center;
  }
`

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: var(--font-weight-semibold);
  font-size: 0.875rem;
  transition: all var(--transition-fast);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`

const ShareIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;

  svg {
    width: 100%;
    height: 100%;
  }
`

const ShareText = styled.span`
  @media screen and (max-width: 600px) {
    display: none;
  }
`

// Shimmer loading styles
const ShimmerImage = styled.div`
  width: 100%;
  border-radius: var(--radius-xl);
  aspect-ratio: 1;
  background: var(--color-surface);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      var(--color-surface) 0%,
      var(--color-surface-elevated) 50%,
      var(--color-surface) 100%
    );
    background-size: 1000px 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`

const ShimmerTitle = styled.div`
  height: 2.5rem;
  border-radius: var(--radius-md);
  background: var(--color-surface);
  position: relative;
  overflow: hidden;
  width: 80%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      var(--color-surface) 0%,
      var(--color-surface-elevated) 50%,
      var(--color-surface) 100%
    );
    background-size: 1000px 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`

const ShimmerMeta = styled.div`
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
`

const ShimmerBadge = styled.div`
  height: 1.5rem;
  width: 80px;
  border-radius: var(--radius-sm);
  background: var(--color-border);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      var(--color-border) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      var(--color-border) 100%
    );
    background-size: 1000px 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`

const ShimmerTabs = styled.div`
  display: flex;
  gap: var(--spacing-md);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`

const ShimmerTab = styled.div`
  height: 1.25rem;
  width: 100px;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      var(--color-surface) 0%,
      var(--color-surface-elevated) 50%,
      var(--color-surface) 100%
    );
    background-size: 1000px 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`

const ShimmerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`

const ShimmerParagraph = styled.div`
  height: 1rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  position: relative;
  overflow: hidden;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      var(--color-surface) 0%,
      var(--color-surface-elevated) 50%,
      var(--color-surface) 100%
    );
    background-size: 1000px 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`

const ShimmerDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--color-border);
  margin: var(--spacing-md) 0;
`

const RecipeMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
`

const MetaIcon = styled.span`
  font-size: 1.125rem;
  display: flex;
  align-items: center;
`


const MetaText = styled.span`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
`

// Serving Adjuster Styles
const ServingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
`

const ServingLabel = styled.span`
  font-size: 0.9375rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  flex: 1;
`

const ServingControls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const ServingButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  color: var(--color-text-primary);
  font-size: 1.25rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    border-color: var(--color-accent);
    color: white;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const ServingValue = styled.span`
  font-size: 1.125rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  min-width: 48px;
  text-align: center;
`

const ScaleIndicator = styled.span`
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent);
`

const ScaledBadge = styled.span`
  font-size: 0.625rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent);
  background: rgba(59, 130, 246, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const DietTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
`

const DietTag = styled.span`
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-accent);
  color: white;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  text-transform: capitalize;
`

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`

const TabContainer = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--spacing-sm);
`

const TabButton = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  color: ${props => props.active ? 'var(--color-accent)' : 'var(--color-text-secondary)'};
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.active ? 'var(--color-accent)' : 'transparent'};
  cursor: pointer;
  transition: all var(--transition-fast);
  position: relative;
  margin-bottom: -1px;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--color-accent);
  }

  @media screen and (max-width: 900px) {
    font-size: 0.9375rem;
    padding: var(--spacing-xs) var(--spacing-md);
  }
`

const TabContent = styled.div`
  min-height: 400px;
`

const InstructionsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`

const SummarySection = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: var(--color-text-primary);

  p {
    margin-bottom: var(--spacing-md);
    color: var(--color-text-secondary);
  }

  a {
    color: var(--color-accent);
    text-decoration: underline;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--color-border);
  margin: var(--spacing-md) 0;
`

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
`

const InstructionsSection = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--color-text-primary);

  ol, ul {
    padding-left: var(--spacing-lg);
    margin-bottom: var(--spacing-md);
  }

  li {
    margin-bottom: var(--spacing-sm);
    color: var(--color-text-secondary);
  }

  p {
    margin-bottom: var(--spacing-md);
    color: var(--color-text-secondary);
  }
`

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`

const StepItem = styled.div`
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  transition: all var(--transition-fast);

  &:hover {
    border-color: var(--color-accent);
    background: var(--color-surface-elevated);
  }
`

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  background: var(--color-accent);
  color: white;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  flex: 1;
`

const StepText = styled.p`
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--color-text-primary);
  margin: 0;
`

const StepMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
`

const StepMetaLabel = styled.span`
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
`

const StepMetaTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const StepMetaTag = styled.span`
  font-size: 0.75rem;
  padding: 2px 8px;
  background: var(--color-surface-elevated);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
`

const IngredientsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`

const IngredientItem = styled.li`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  transition: all var(--transition-fast);

  &:hover {
    background: var(--color-background);
    border-color: var(--color-accent);
    transform: translateX(4px);
  }
`

const IngredientBullet = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-accent);
  flex-shrink: 0;
`

const IngredientText = styled.span`
  font-size: 0.9375rem;
  line-height: 1.4;
  color: var(--color-text-primary);
  flex: 1;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: var(--spacing-lg);
`

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid var(--color-border);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingText = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0;
`

const EmptyState = styled.div`
  padding: var(--spacing-xl);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 1rem;
`

const NutritionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`

const NutritionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--spacing-md);
`

const NutritionItem = styled.div`
  padding: var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`

const NutritionLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
`

const NutritionValue = styled.div`
  font-size: 1.25rem;
  color: var(--color-text-primary);
  font-weight: var(--font-weight-bold);
`

const DetailsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`

const DetailLabel = styled.div`
  font-size: 1rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-xs);
`

const DetailTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
`

const DetailTag = styled.span`
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: var(--font-weight-medium);
  text-transform: capitalize;
  border: 1px solid var(--color-border);
`

const WinePairingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`

const WineList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
`

const WineItem = styled.span`
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-accent);
  color: white;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: var(--font-weight-medium);
  text-transform: capitalize;
`

const WineDescription = styled.p`
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin: 0;
  padding: var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
`

const NutritionPercent = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-normal);
`

// Equipment styles
const EquipmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: var(--spacing-sm);
`

const EquipmentItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  text-align: center;
  transition: all var(--transition-fast);

  &:hover {
    border-color: var(--color-accent);
    transform: translateY(-2px);
  }
`

const EquipmentImageContainer = styled.div`
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
`

const EquipmentImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`

const EquipmentName = styled.span`
  font-size: 0.75rem;
  color: var(--color-text-primary);
  text-transform: capitalize;
`

// Taste profile styles
const TasteGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`

const TasteItem = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 40px;
  align-items: center;
  gap: var(--spacing-sm);
`

const TasteLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
`

const TasteBarContainer = styled.div`
  height: 8px;
  background: var(--color-surface);
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 1px solid var(--color-border);
`

const TasteBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--color-accent), #ff6b6b);
  border-radius: var(--radius-sm);
  transition: width 0.5s ease;
`

const TasteValue = styled.span`
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  text-align: right;
`

// Price styles
const PriceInfo = styled.div`
  display: flex;
  gap: var(--spacing-md);
  flex-wrap: wrap;
`

const PriceItem = styled.div`
  padding: var(--spacing-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  flex: 1;
  min-width: 120px;
`

const PriceLabel = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  margin-bottom: var(--spacing-xs);
`

const PriceValue = styled.div`
  font-size: 1.25rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-accent);
`

// Similar recipes styles
const SimilarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: var(--spacing-sm);
  align-items: stretch;
`

const SimilarCardWrapper = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  overflow: hidden;
  transition: all var(--transition-fast);
  height: 100%;

  &:hover {
    border-color: var(--color-accent);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--color-shadow);
  }
`

const SimilarImage = styled.img`
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  background: var(--color-border);
`

const SimilarTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  padding: var(--spacing-xs);
  padding-bottom: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`

const SimilarMeta = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  padding: var(--spacing-xs);
  padding-top: 0;
  display: flex;
  gap: var(--spacing-xs);
  margin-top: auto;
`

export default Recipe
